/**
 * Module-scoped registry that bridges {@link TeamSelectionProvider} (which
 * lives below {@link ApolloClientProvider} in the tree) and the Apollo link
 * that injects the `x-pov-team-id` request header.
 *
 * The provider can't be a context dependency of the Apollo client without
 * inverting the tree, so it pushes its current value into this registry on
 * every change. The link reads from it for each outgoing operation.
 *
 * Until the provider has pushed its first snapshot, `hasSynced` is `false`
 * and the link defers every operation. This is deliberate: the registry's
 * initial `mode` is `hidden`, but on a hard refresh that default is a lie —
 * a demo org's real mode isn't known until `CurrentUserProvider` resolves
 * `me` and `TeamSelectionProvider` reports in. Without the `hasSynced` gate
 * the first home queries race ahead of that sync, get sent header-less, and
 * the cache-first org-default response sticks (SE-4982).
 *
 * The header name is intentionally re-declared here (matching
 * `backend-v2/src/team-context/decorators/pov-team-id.decorator.ts`) rather
 * than imported from `@/generated/graphql`, because it's a transport-level
 * contract not part of the schema.
 * @module providers/povTeamHeaderState
 */
import type { TeamSelectionMode } from "@/generated/graphql";

/**
 * HTTP header that carries the demo-flex POV (point-of-view) active-team
 * UUID. Mirrors the `POV_TEAM_ID_HEADER` constant exported from the
 * `@PovTeamId()` decorator in `backend-v2`.
 */
export const POV_TEAM_ID_HEADER = "x-pov-team-id";

const HIDDEN_MODE = "hidden" as TeamSelectionMode;

/**
 * Snapshot pushed by {@link TeamSelectionProvider}. The provider does not
 * know about `hasSynced` — that flag is owned by this module and flipped
 * to `true` the first time {@link setPovTeamHeaderState} runs.
 */
export interface PovTeamSnapshot {
  /** Active org's team-selection mode. Drives whether the link can short-circuit. */
  mode: TeamSelectionMode;
  /** Currently selected team UUID, or null when no team is active. */
  activeTeamId: string | null;
  /** True once {@link TeamSelectionProvider} has rehydrated from AsyncStorage. */
  isLoaded: boolean;
}

/**
 * Internal registry state: the provider snapshot plus the module-owned
 * `hasSynced` flag.
 */
interface State extends PovTeamSnapshot {
  /**
   * True once {@link TeamSelectionProvider} has pushed at least one
   * snapshot. While false, the initial `mode`/`isLoaded` values are
   * placeholder defaults the link must not act on.
   */
  hasSynced: boolean;
}

// eslint-disable-next-line functional/no-let -- module-scoped registry populated by TeamSelectionProvider
let state: State = {
  mode: HIDDEN_MODE,
  activeTeamId: null,
  // `isLoaded` defaults to true so that, once `hasSynced` flips, a provider
  // that never enters the hydrating phase (no signed-in user) is immediately
  // ready. It does NOT mean "ready" on its own — see `isReady`.
  isLoaded: true,
  // The link defers every operation until the provider reports in.
  hasSynced: false,
};

// eslint-disable-next-line functional/no-let -- waiters are flushed on the next ready transition
let waiters: (() => void)[] = [];

/**
 * The registry is "ready" — safe for the link to forward against — once the
 * provider has reported in AND selection has finished hydrating from
 * AsyncStorage.
 * @param snapshot - The state snapshot to test
 * @returns True when the link may forward (synchronously or after a wait)
 */
function isReady(snapshot: State): boolean {
  return snapshot.hasSynced && snapshot.isLoaded;
}

/**
 * Replaces the registry with a new snapshot. Called by
 * {@link TeamSelectionProvider} on every value change. Marks the registry
 * as synced and flushes any operations queued via
 * {@link waitForPovTeamSelectionReady} once the registry becomes ready.
 * @param next - The new snapshot from the provider
 */
export function setPovTeamHeaderState(next: PovTeamSnapshot): void {
  const wasReady = isReady(state);
  state = { ...next, hasSynced: true };
  if (!wasReady && isReady(state) && waiters.length > 0) {
    const queued = waiters;
    waiters = [];
    queued.forEach(resolve => resolve());
  }
}

/**
 * Returns the current snapshot. Read by the Apollo link on each outgoing
 * operation.
 * @returns The current state snapshot
 */
export function getPovTeamHeaderState(): State {
  return state;
}

/**
 * Resolves once the registry is ready (the provider has synced and selection
 * has finished loading). Used by the Apollo link to defer operations until
 * then, so we never send a header-less request that would later be served
 * stale by the cache once the real POV is known.
 * @returns A promise that resolves when team selection is ready
 */
export function waitForPovTeamSelectionReady(): Promise<void> {
  if (isReady(state)) {
    return Promise.resolve();
  }
  return new Promise(resolve => {
    waiters.push(resolve);
  });
}

/**
 * Test-only helper. Resets the registry to its initial values so tests can
 * exercise the link in isolation without leaking state across cases.
 */
export function __resetPovTeamHeaderStateForTests(): void {
  state = {
    mode: HIDDEN_MODE,
    activeTeamId: null,
    isLoaded: true,
    hasSynced: false,
  };
  waiters = [];
}
